import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import WideBinSetStatusCard from '../../organisms/WideBinSetStatusCard';
import useUser from '../../utils/hooks/useUser';
import { useCensor, censorTypes } from '../../utils/hooks/useCensor';
import useFeature from '../../utils/hooks/useFeature';
import { processBinSetOrders } from '../../utils/orderUtils';
import { Privilege, useCan } from '@feedflo/access-control';
import dayjs from 'dayjs';
import BinSetLevelPredictionsExportButton from './BinSetLevelPredictionsExportButton';
import '../AllBinsPage/AllBinsPage.scss'; // Reuse most styling from AllBinsPage
import './InventoryTabV2.scss'; // New styles for InventoryTabV2

const BARN_BIN_SETS_GQL = gql`
  query InventoryTabV2_BarnBinSetsQuery($farmId: uuid!) {
    farm(where: { id: { _eq: $farmId }, deleted_at: { _is_null: true } }) {
      id
      name
      bin_sets(
        where: { deleted_at: { _is_null: true } }
        order_by: [{ bins_aggregate: { min: { name: asc } } }, { created_at: desc }]
      ) {
        id
        bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
          id
          name
          capacity_in_grams
          last_delivery: deliveries(
            where: { status: { _eq: "delivered" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          scheduled_delivery: deliveries(
            where: { status: { _eq: "ordered" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          recommended_delivery: deliveries(
            where: { status: { _eq: "recommended" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          shipped_delivery: deliveries(
            where: { status: { _eq: "shipped" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
        }
        feed_lines(where: { deleted_at: { _is_null: true } }) {
          name
        }
        bin_set_level_predictions(
          where: {
            deleted_at: { _is_null: true },
            version: { _in: ["3.0"] },
          }
          order_by: { created_at: desc }
          limit: 1
        ) {
          created_at
          predicted_at
          bin_set_level_in_grams
          empty_at
          earliest_delivery_at
          runout_prevention_order_deadline_at
          runout_prevention_bin_set_level_in_grams
          earliest_delivery_bin_set_level_in_grams
          past_feed_inventory_time_series
          delivered_deliveries_projection_time_series
          scheduled_deliveries_projection_time_series
          recommended_deliveries_projection_time_series
          per_bin_past_feed_inventory_time_series
          per_bin_delivered_deliveries_projection_time_series
          per_bin_scheduled_deliveries_projection_time_series
          per_bin_recommended_deliveries_projection_time_series
          per_bin_level_in_grams
        }
      }
    }
  }
`;

const KNOWN_BIN_LEVEL_GQL = gql`
  query KnownBinLevelQuery($binSetId: uuid!, $startAt: bigint!, $endAt: bigint!) {
    known_bin_level: time_sample_known_bin_levels_for_bin_set(
      args: { interval_in_seconds: 3600, bin_set_id: $binSetId, start_at: $startAt, end_at: $endAt }
    ) {
      valid_at: window_start
      level_in_grams: median_total_level
    }
  }
`;

// Component to handle individual bin set truth data queries
function BinSetWithTruthData({ binSet, censor, isMetric, startDateTime, endDateTime, displayTruthData }) {
  const { data: knownBinLevelData } = useQuery(KNOWN_BIN_LEVEL_GQL, {
    variables: {
      binSetId: binSet.id,
      startAt: startDateTime.unix(),
      endAt: endDateTime.unix(),
    },
    skip: !displayTruthData,
    fetchPolicy: 'cache-and-network',
  });

  const { bins, order } = processBinSetOrders(binSet, censor);
  const feedLineNames = binSet.feed_lines.map((fl) => censor(fl.name, censorTypes.feedline));
  const prediction = binSet.bin_set_level_predictions[0] || {};
  const perBinChartData = {
    pastByBinId: prediction.per_bin_past_feed_inventory_time_series || null,
    deliveredByBinId: prediction.per_bin_delivered_deliveries_projection_time_series || null,
    scheduledByBinId: prediction.per_bin_scheduled_deliveries_projection_time_series || null,
    recommendedByBinId: prediction.per_bin_recommended_deliveries_projection_time_series || null,
  };

  return (
    <WideBinSetStatusCard
      key={binSet.id}
      binSetId={binSet.id}
      bins={bins}
      perBinChartData={perBinChartData}
      perBinLevelInGrams={prediction.per_bin_level_in_grams}
      feedLineNames={feedLineNames}
      binSetLevelInGrams={prediction.bin_set_level_in_grams}
      predictedAt={prediction.predicted_at}
      emptyAt={prediction.empty_at}
      earliestDeliveryAt={prediction.earliest_delivery_at}
      runout_prevention_order_deadline_at={prediction.runout_prevention_order_deadline_at}
      runout_prevention_bin_set_level_in_grams={prediction.runout_prevention_bin_set_level_in_grams}
      earliest_delivery_bin_set_level_in_grams={prediction.earliest_delivery_bin_set_level_in_grams}
      isMetric={isMetric}
      order={order}
      lastUpdatedAt={prediction.created_at}
      step_bin_level_history={prediction.past_feed_inventory_time_series || []}
      projection_past_deliveries={prediction.delivered_deliveries_projection_time_series || []}
      projection_with_scheduled={prediction.scheduled_deliveries_projection_time_series || []}
      projection_with_recommended={prediction.recommended_deliveries_projection_time_series || []}
      chartStartTime={startDateTime.unix()}
      chartEndTime={endDateTime.unix()}
      knownBinLevelData={knownBinLevelData?.known_bin_level || []}
      displayTruthData={displayTruthData}
    />
  );
}

BinSetWithTruthData.propTypes = {
  binSet: PropTypes.object.isRequired,
  censor: PropTypes.func.isRequired,
  isMetric: PropTypes.bool.isRequired,
  startDateTime: PropTypes.object.isRequired,
  endDateTime: PropTypes.object.isRequired,
  displayTruthData: PropTypes.bool.isRequired,
};

function InventoryTabV2({ titleSegments = [], barnId = '' }) {
  const { user } = useUser();
  const { censor } = useCensor();
  const { active: displayTruthData } = useFeature('DISPLAY_TRUTH_DATA');
  const { can } = useCan();
  const isMetric = user?.isMetric || false;

  // Check if user can export data for this barn
  const canExportBarn = can('barn', barnId, Privilege.BARN_EXPORT);

  const { loading, error, data } = useQuery(BARN_BIN_SETS_GQL, {
    variables: { farmId: barnId },
    fetchPolicy: 'cache-and-network',
  });

  const startDateTime = useMemo(() => dayjs().subtract(4, 'week'), []);
  const endDateTime = useMemo(() => dayjs().add(4, 'week'), []);

  const pageTitleSegments = useMemo(() => ['Inventory V2', ...titleSegments], [titleSegments]);

  const barn = data?.farm?.[0];

  if (error) {
    throw error;
  }

  const renderContent = () => {

    // Show loading
    if (loading) {
      return (
        <div className="AllBinsPage-loading">
          <span>Loading...</span>
        </div>
      );
    }

    // Show no bin sets found
    if (!barn || !barn.bin_sets || barn.bin_sets.length === 0) {
      return (
        <div className="AllBinsPage-noResults">
          <h3>No Bin Sets Found</h3>
          <p>No bin sets found for this barn.</p>
        </div>
      );
    }

    return (
      <Card className="InventoryTabV2-binSetCard" contentClassName="InventoryTabV2-binSetCardContent">
        <div className="AllBinsPage-binSets">
          {barn.bin_sets.map((binSet) => (
            <BinSetWithTruthData
              key={binSet.id}
              binSet={binSet}
              censor={censor}
              isMetric={isMetric}
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              displayTruthData={displayTruthData}
            />
          ))}
        </div>
      </Card>
    );
  };

  return (
    <Page className="InventoryTabV2" titleSegments={pageTitleSegments}>
      {canExportBarn && (
        <div className="InventoryTabV2-header">
          <div className="InventoryTabV2-exportButton">
            <BinSetLevelPredictionsExportButton barnId={barnId} barnName={barn?.name || 'Unknown'} />
          </div>
        </div>
      )}
      <div className="AllBinsPage-content">{renderContent()}</div>
    </Page>
  );
}

InventoryTabV2.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  barnId: PropTypes.string,
};

export default InventoryTabV2;
